import { useEffect, useRef } from 'react';

/**
 * Custom hook to handle modal closing via ESC key and browser back button
 *
 * @param {Function} onClose - Callback function to close the modal
 * @param {boolean} isOpen - Whether the modal is currently open (optional, defaults to true)
 *
 * Usage:
 * function MyModal({ onClose }) {
 *   useModalClose(onClose);
 *   // ... rest of component
 * }
 */
export function useModalClose(onClose, isOpen = true) {
    const closedViaBackButton = useRef(false);
    const historyPushed = useRef(false);
    const modalId = useRef(Date.now()); // Unique ID for this modal instance

    useEffect(() => {
        if (!isOpen) return;

        // Reset the flags when modal opens
        closedViaBackButton.current = false;
        historyPushed.current = false;
        modalId.current = Date.now(); // Generate new unique ID on each open

        // Handle ESC key press
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        // Handle browser back button
        const handlePopState = (event) => {
            // Only respond to popstate if we actually pushed a state
            if (!historyPushed.current) return;

            // Check if this popstate is for our modal (not another modal's state)
            if (event.state && event.state.modalId === modalId.current) {
                // Mark that we're closing via back button
                closedViaBackButton.current = true;
                onClose();
            }
        };

        // Add ESC key listener immediately
        document.addEventListener('keydown', handleEscKey);

        // Push a state to history so back button can be detected
        // Use a longer timeout to ensure modal is fully rendered before pushing state
        const timeoutId = setTimeout(() => {
            // Only push state if modal is still open
            if (isOpen) {
                window.history.pushState({ modal: true, modalId: modalId.current }, '');
                historyPushed.current = true;
                // Add popstate listener AFTER pushing state to avoid immediate trigger
                window.addEventListener('popstate', handlePopState);
            }
        }, 100); // Increased from 0 to 100ms to avoid race conditions

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('keydown', handleEscKey);
            window.removeEventListener('popstate', handlePopState);

            // Only go back if the modal was NOT closed via back button
            // This prevents double-back and allows modal to reopen
            if (historyPushed.current && window.history.state && window.history.state.modalId === modalId.current && !closedViaBackButton.current) {
                window.history.back();
            }
        };
    }, [onClose, isOpen]);
}
